import { apiClient } from './api';
import { useAuthStore } from '@/stores/auth';

// VERACODE-MITIGATION: CWE-918 - All API requests use the environment-configured base URL
// (VITE_API_BASE_URL) via the shared API client. URLs are not user-controlled.

// The investigation-service captures audit-log attribution from these body fields when the
// service-side auth middleware isn't mounted (current setup — JWT is validated at the gateway).
function getActingUserMeta(): { actingUserName?: string; actingUserEmail?: string } {
  const user = useAuthStore.getState().user;
  if (!user) {return {};}
  const name = user.fullName ?? (`${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || user.email);
  return {
    actingUserName: name || undefined,
    actingUserEmail: user.email || undefined,
  };
}

interface SubjectListResponse {
  subjects: Subject[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export type SubjectRoleType =
  | 'SUSPECT'
  | 'VICTIM'
  | 'WITNESS'
  | 'LAW_ENFORCEMENT'
  | 'REPORTING_PARTY'
  | 'OTHER';

export type SubjectRiskLevel = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';

export type SubjectCaseType = 'INVESTIGATION' | 'INCIDENT';

export type SubjectHistoryAction =
  | 'CREATE'
  | 'UPDATE'
  | 'DELETE'
  | 'LINK_INVESTIGATION'
  | 'UNLINK_INVESTIGATION'
  | 'LINK_INCIDENT'
  | 'UNLINK_INCIDENT';

export interface SocialMediaAccount {
  platform: string;
  handle?: string;
  url?: string;
}

export interface SubjectCaseLink {
  id: string;
  subjectId: string;
  caseId: string;
  caseType: SubjectCaseType;
  // VIS-26 fix F: backend (investigation-service subject controller) enriches each
  // link with the human-readable case identifier and title; either may be null when
  // the underlying case has been soft-deleted or is unreachable cross-service.
  caseNumber: string | null;
  title: string | null;
  caseRole?: string | null;
  relationshipToCase?: string | null;
  linkedByUserId: string;
  linkedByName: string;
  linkedAt: string;
  unlinkedAt?: string | null;
  unlinkedReason?: string | null;
  notes?: string | null;
}

export interface Subject {
  id: string;
  firstName: string;
  lastName: string;
  roleType: SubjectRoleType;
  title?: string | null;
  photoUrl?: string | null;
  email?: string | null;
  phone?: string | null;
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
  country?: string | null;
  riskLevel?: SubjectRiskLevel | null;
  distanceFromOfficeMiles?: number | null;
  vehicleMake?: string | null;
  vehicleModel?: string | null;
  vehicleColor?: string | null;
  vehiclePlate?: string | null;
  socialMedia?: SocialMediaAccount[] | null;
  criminalHistory?: string | null;
  supervisor?: string | null;
  agency?: string | null;
  jurisdiction?: string | null;
  badgeNumber?: string | null;
  notes?: string | null;
  createdBy: string;
  createdByName: string;
  createdByEmail: string;
  createdAt: string;
  updatedAt: string;
  deletedAt?: string | null;
  linkedInvestigationsCount?: number;
  linkedIncidentsCount?: number;
  investigationLinks?: SubjectCaseLink[];
  incidentLinks?: SubjectCaseLink[];
}

export interface SubjectHistoryEntry {
  id: string;
  subjectId: string;
  action: SubjectHistoryAction;
  fieldChanges?: Record<string, { from: unknown; to: unknown }> | null;
  linkedCaseId?: string | null;
  linkedCaseType?: SubjectCaseType | null;
  userId: string;
  userName: string;
  userEmail: string;
  createdAt: string;
}

export interface SubjectStats {
  total: number;
  byRole: Record<SubjectRoleType, number>;
  byRisk: Record<SubjectRiskLevel, number>;
  createdInLast30Days: number;
}

export interface CreateSubjectData {
  firstName: string;
  lastName: string;
  roleType: SubjectRoleType;
  title?: string;
  photoUrl?: string;
  email?: string;
  phone?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
  riskLevel?: SubjectRiskLevel;
  distanceFromOfficeMiles?: number;
  vehicleMake?: string;
  vehicleModel?: string;
  vehicleColor?: string;
  vehiclePlate?: string;
  socialMedia?: SocialMediaAccount[];
  criminalHistory?: string;
  supervisor?: string;
  agency?: string;
  jurisdiction?: string;
  badgeNumber?: string;
  notes?: string;
}

export type UpdateSubjectData = Partial<CreateSubjectData>;

export interface LinkSubjectData {
  caseId: string;
  caseType: SubjectCaseType;
  caseRole?: string;
  relationshipToCase?: string;
  notes?: string;
}

export interface GetSubjectsParams {
  page?: number;
  limit?: number;
  search?: string;
  roleType?: SubjectRoleType | 'all';
  riskLevel?: SubjectRiskLevel | 'all';
  investigationId?: string;
  incidentId?: string;
  sortField?: 'createdAt' | 'updatedAt' | 'lastName' | 'riskLevel';
  sortDirection?: 'asc' | 'desc';
}

export const SUBJECT_ROLE_FIELDS: Record<SubjectRoleType, Array<keyof CreateSubjectData>> = {
  SUSPECT: [
    'firstName', 'lastName', 'roleType', 'title', 'photoUrl', 'email', 'phone',
    'addressLine1', 'addressLine2', 'city', 'state', 'postalCode', 'country',
    'riskLevel', 'distanceFromOfficeMiles',
    'vehicleMake', 'vehicleModel', 'vehicleColor', 'vehiclePlate',
    'socialMedia', 'criminalHistory', 'notes',
  ],
  VICTIM: [
    'firstName', 'lastName', 'roleType', 'title', 'photoUrl', 'email', 'phone',
    'addressLine1', 'city', 'state', 'postalCode', 'country', 'supervisor', 'notes',
  ],
  WITNESS: [
    'firstName', 'lastName', 'roleType', 'title', 'photoUrl', 'email', 'phone',
    'addressLine1', 'city', 'state', 'postalCode', 'country', 'supervisor', 'notes',
  ],
  LAW_ENFORCEMENT: [
    'firstName', 'lastName', 'roleType', 'title', 'photoUrl', 'email', 'phone',
    'agency', 'jurisdiction', 'badgeNumber', 'notes',
  ],
  REPORTING_PARTY: [
    'firstName', 'lastName', 'roleType', 'title', 'photoUrl', 'email', 'phone',
    'addressLine1', 'city', 'state', 'postalCode', 'country', 'supervisor', 'notes',
  ],
  OTHER: [
    'firstName', 'lastName', 'roleType', 'title', 'photoUrl', 'email', 'phone',
    'addressLine1', 'city', 'state', 'postalCode', 'country', 'supervisor', 'notes',
  ],
};

class SubjectsService {
  async getSubjects(params: GetSubjectsParams = {}): Promise<{
    subjects: Subject[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  }> {
    const queryParams = new URLSearchParams();

    if (params.page) { queryParams.append('page', params.page.toString()); }
    if (params.limit) { queryParams.append('limit', params.limit.toString()); }
    if (params.search) { queryParams.append('search', params.search); }
    if (params.roleType && params.roleType !== 'all') { queryParams.append('roleType', params.roleType); }
    if (params.riskLevel && params.riskLevel !== 'all') { queryParams.append('riskLevel', params.riskLevel); }
    if (params.investigationId) { queryParams.append('investigationId', params.investigationId); }
    if (params.incidentId) { queryParams.append('incidentId', params.incidentId); }
    if (params.sortField) { queryParams.append('sortField', params.sortField); }
    if (params.sortDirection) { queryParams.append('sortDirection', params.sortDirection); }

    const url = `/api/v1/subjects${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await apiClient.get<SubjectListResponse>(url);

    if (response.success && response.data) {
      const { subjects, pagination } = response.data;
      return {
        subjects,
        total: pagination?.total ?? subjects.length,
        page: pagination?.page ?? 1,
        limit: pagination?.limit ?? 20,
        totalPages: pagination?.totalPages ?? 1,
      };
    }
    throw new Error(response.error?.message ?? 'Failed to fetch subjects');
  }

  async getSubjectById(id: string): Promise<Subject> {
    const response = await apiClient.get<Subject>(`/api/v1/subjects/${id}`);
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error?.message ?? 'Failed to fetch subject');
  }

  async createSubject(data: CreateSubjectData): Promise<Subject> {
    const response = await apiClient.post<Subject>('/api/v1/subjects', { ...data, ...getActingUserMeta() });
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error?.message ?? 'Failed to create subject');
  }

  async updateSubject(id: string, data: UpdateSubjectData): Promise<Subject> {
    const response = await apiClient.put<Subject>(`/api/v1/subjects/${id}`, { ...data, ...getActingUserMeta() });
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error?.message ?? 'Failed to update subject');
  }

  async deleteSubject(id: string): Promise<void> {
    const response = await apiClient.delete<void>(`/api/v1/subjects/${id}`, { data: getActingUserMeta() });
    if (!response.success) {
      throw new Error(response.error?.message ?? 'Failed to delete subject');
    }
  }

  async uploadSubjectPhoto(id: string, file: File, onProgress?: (progress: number) => void): Promise<string | null> {
    const response = await apiClient.uploadFile<{ photoUrl: string }>(
      `/api/v1/subjects/${id}/photo`,
      file,
      onProgress,
      getActingUserMeta(),
    );
    return response.success && response.data ? response.data.photoUrl : null;
  }

  async getSubjectStats(): Promise<SubjectStats> {
    const response = await apiClient.get<SubjectStats>('/api/v1/subjects/stats');
    if (response.success && response.data) {
      return response.data;
    }
    throw new Error(response.error?.message ?? 'Failed to fetch subject stats');
  }

  async getSubjectHistory(id: string): Promise<SubjectHistoryEntry[]> {
    const response = await apiClient.get<{ history: SubjectHistoryEntry[] }>(
      `/api/v1/subjects/${id}/history`,
    );
    if (response.success && response.data) {
      return response.data.history;
    }
    throw new Error(response.error?.message ?? 'Failed to fetch subject history');
  }

  async linkSubjectToCase(subjectId: string, link: LinkSubjectData): Promise<SubjectCaseLink> {
    const response = await apiClient.post<{ link: SubjectCaseLink }>(
      `/api/v1/subjects/${subjectId}/links`,
      { ...link, ...getActingUserMeta() },
    );
    if (response.success && response.data) {
      return response.data.link;
    }
    throw new Error(response.error?.message ?? 'Failed to link subject');
  }

  async unlinkSubjectFromCase(
    subjectId: string,
    caseType: SubjectCaseType,
    caseId: string,
    unlinkedReason?: string,
  ): Promise<void> {
    const response = await apiClient.delete<void>(
      `/api/v1/subjects/${subjectId}/links/${caseType}/${caseId}`,
      { data: { ...(unlinkedReason ? { unlinkedReason } : {}), ...getActingUserMeta() } },
    );
    if (!response.success) {
      throw new Error(response.error?.message ?? 'Failed to unlink subject');
    }
  }

  getRoleTypeConfig(roleType: SubjectRoleType) {
    const config: Record<SubjectRoleType, { color: string; label: string }> = {
      SUSPECT: { color: 'danger', label: 'Suspect' },
      VICTIM: { color: 'warning', label: 'Victim' },
      WITNESS: { color: 'primary', label: 'Witness' },
      LAW_ENFORCEMENT: { color: 'secondary', label: 'Law Enforcement' },
      REPORTING_PARTY: { color: 'success', label: 'Reporting Party' },
      OTHER: { color: 'secondary', label: 'Other' },
    };
    return config[roleType] ?? { color: 'secondary', label: roleType };
  }

  getRiskLevelConfig(riskLevel: SubjectRiskLevel | null | undefined) {
    if (!riskLevel) { return { color: 'secondary', label: 'Unknown' }; }
    const config: Record<SubjectRiskLevel, { color: string; label: string }> = {
      CRITICAL: { color: 'danger', label: 'Critical' },
      HIGH: { color: 'warning', label: 'High' },
      MEDIUM: { color: 'primary', label: 'Medium' },
      LOW: { color: 'success', label: 'Low' },
    };
    return config[riskLevel] ?? { color: 'secondary', label: riskLevel };
  }

  // Static class strings so Tailwind's JIT compiler can detect them.
  // Dynamic interpolation like `bg-${color}-100` would be tree-shaken in production.
  getBadgeClasses(color: string): string {
    const map: Record<string, string> = {
      danger: 'bg-danger-100 text-danger-700 dark:bg-danger-900/20 dark:text-danger-400',
      warning: 'bg-warning-100 text-warning-700 dark:bg-warning-900/20 dark:text-warning-400',
      primary: 'bg-primary-100 text-primary-700 dark:bg-primary-900/20 dark:text-primary-400',
      success: 'bg-success-100 text-success-700 dark:bg-success-900/20 dark:text-success-400',
      secondary: 'bg-secondary-100 text-secondary-700 dark:bg-secondary-900/20 dark:text-secondary-400',
    };
    return map[color] ?? map.secondary;
  }

  getSubjectDisplayName(subject: Pick<Subject, 'firstName' | 'lastName' | 'title'>): string {
    const name = `${subject.firstName} ${subject.lastName}`.trim();
    return subject.title ? `${subject.title} ${name}`.trim() : name;
  }

  formatTimestamp(timestamp: string): string {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));

    if (diffInMinutes < 1) { return 'Just now'; }
    if (diffInMinutes < 60) { return `${diffInMinutes} minutes ago`; }

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) { return `${diffInHours} hours ago`; }

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 30) { return `${diffInDays} days ago`; }

    return date.toLocaleDateString();
  }
}

export const subjectsService = new SubjectsService();
export default subjectsService;
